import { useState } from 'react';
import { useGetInvoiceId } from '@/core/domain/invoice/invoice/invoice.hooks';
import { useDisclosure } from '@/hooks';
import {
  Button,
  Divider,
  Form,
  Modal,
  Space,
  Table,
  Tooltip,
  Typography,
} from 'antd';
import { Eye } from 'react-feather';
import { useIntl } from 'react-intl';
import { useHistory } from 'react-router-dom';
import { PaymentMethodTranslate } from '@/core/domain/invoice/PaymentMethod/PaymentMethod.types';
import NewPaymentModal from '@/components/Fiscal/Invoice/InvoiceForms/InvoicePaymentConditionGeneralForm/InvoicePaymentCondition/NewPaymentModal';
import {
  PaymentMethodCreateRequestDTO,
  PaymentMethodEnum,
} from '@/core/domain/invoice/PaymentMethod/PaymentMethod.types';
import {
  useDeletePaymentMethodInvoice,
  usePaymentMethods,
} from '@/core/domain/invoice/PaymentMethod/PaymentMethod.hook';
import { flatInfiniteData } from '@/utils/request/flatInfiniteData';
import { ColumnsType } from 'antd/lib/table';
import { DeleteIcon, EditIcon } from '@/components/_common';
import { quantityFormatter } from '@/utils/number';
import SpaceForButton from '@/components/_common/SpaceForButtons';
import { getTableScrollX } from '@/utils/table';
import InvoiceUpdateCfopModal from '@/components/Fiscal/Invoice/InvoiceUpdateModal/InvoiceUpdateCfopModal';

interface DirectSaleTransmitInvoiceModalProps {
  visible: boolean;
  onCancel: () => void;
  invoiceId: number;
}

export default function DirectSaleTransmitInvoiceModal({
  visible,
  onCancel,
  invoiceId,
}: DirectSaleTransmitInvoiceModalProps) {
  const intl = useIntl().formatMessage;
  const history = useHistory();
  const [transmitAddPayment, setTransmitAddPayment] = useDisclosure();
  const [transmit, setTransmit] = useDisclosure();
  const { data: invoiceData, isFetching: invoiceFetching } =
    useGetInvoiceId(invoiceId);

  const translate = (message: string, type: 'column' | 'button' = 'column') =>
    intl({ id: `invoicePaymentCondition.${type}.${message}` });

  const [form] = Form.useForm();

  const { data } = usePaymentMethods(invoiceId);

  const paymentMethods = flatInfiniteData(data);

  const [paymentId, setPaymentId] = useState<number | undefined>();
  const { mutateAsync: deleteMutateAsync } = useDeletePaymentMethodInvoice();

  const handleRemove = (id: number) => {
    Modal.confirm({
      title: 'Remover pagamento',
      content:
        'Deseja realmente remover o pagamento?Esta ação é irreversível e não será possível recuperar o pagamento posteriormente. Tem certeza de que deseja continuar?',
      onOk: async () => {
        await deleteMutateAsync({ invoiceId: Number(invoiceId), id });
      },
      okType: 'danger',
      okText: intl({ id: 'commons.entity.remove' }),
    });
  };

  const columns: ColumnsType<PaymentMethodCreateRequestDTO> = [
    {
      title: translate('paymentType'),
      dataIndex: 'paymentMethod',
      key: 'paymentMethod',
      render: (value: PaymentMethodEnum) => {
        return PaymentMethodTranslate[value];
      },
      width: 115,
    },
    {
      title: translate('value'),
      dataIndex: 'value',
      key: 'value',
      render: (value) => quantityFormatter(value),
      width: 90,
      ellipsis: true,
    },
    {
      title: translate('flagCard'),
      dataIndex: 'creditCardBrand',
      key: 'creditCardBrand',
      render: (value) => value ?? '-',
      width: 115,
      ellipsis: true,
    },
    {
      title: translate('authorizationNumber'),
      dataIndex: 'authorizationNumber',
      key: 'authorizationNumber',
      render: (value) => value ?? '-',
      width: 115,
      ellipsis: true,
    },
    {
      title: intl({
        id: 'commons.list.header.action',
      }),
      render: (_, row) => (
        <Space>
          <EditIcon
            iconProps={{
              onClick: () => {
                setPaymentId(row.id);
                setTransmitAddPayment.toggle();
              },
            }}
          />
          <Divider type="vertical" />
          <DeleteIcon onClick={() => handleRemove(Number(row.id))} />
        </Space>
      ),
      width: 100,
      fixed: 'right',
      align: 'right',
    },
  ];

  return (
    <Modal
      width={800}
      title={intl({ id: 'directSale.transmitModal.title' })}
      open={visible}
      onCancel={onCancel}
      footer={
        <SpaceForButton justifyContent="end">
          <Button block onClick={onCancel}>
            {intl({ id: 'commons.form.button.close' })}
          </Button>

          <Button type="primary" block onClick={setTransmitAddPayment.open}>
            {intl({ id: 'directSale.transmitModal.button.addPayment' })}
          </Button>

          <Tooltip
            title={
              !paymentMethods.length &&
              'Necessário adicionar pagamento para transmitir a nota fiscal.'
            }
          >
            <Button
              disabled={!paymentMethods.length}
              onClick={setTransmit.open}
              type="primary"
              block
            >
              {intl({ id: 'directSale.transmitModal.button.transmitInvoice' })}
            </Button>
          </Tooltip>
        </SpaceForButton>
      }
    >
      <Typography.Paragraph>
        {intl({ id: 'directSale.transmitModal.description' })}
      </Typography.Paragraph>

      {!invoiceFetching && (
        <Typography.Paragraph style={{ display: 'flex' }}>
          {intl(
            { id: 'directSale.transmitModal.description.invoiceNumber' },
            { number: invoiceData?.id },
          )}
          <Tooltip title="Clique para visualizar a nota fiscal.">
            <Eye
              size={20}
              style={{ marginLeft: '8px' }}
              cursor="pointer"
              onClick={() =>
                history.push(
                  `/app/fiscal/invoice/${invoiceData?.invoiceType}/${invoiceId}/datas`,
                )
              }
            />
          </Tooltip>
        </Typography.Paragraph>
      )}

      <Table<PaymentMethodCreateRequestDTO>
        scroll={{ x: getTableScrollX(columns) }}
        columns={columns}
        pagination={false}
        dataSource={paymentMethods}
      />

      <NewPaymentModal
        form={form}
        paymentId={paymentId}
        setPaymentId={setPaymentId}
        paymentModalVisible={transmitAddPayment}
        setPaymentModalVisible={setTransmitAddPayment.toggle}
        invoiceId={invoiceId}
      />

      <InvoiceUpdateCfopModal
        cfopModalVisible={transmit}
        setCfopModalVisible={setTransmit.toggle}
        invoiceId={invoiceId}
        invoiceType={invoiceData?.invoiceType}
      />
    </Modal>
  );
}
